import React, { useEffect, useMemo, useRef, useState } from 'react';
import Matchup, { matchSides } from "../../ui/Matchup";
import { eid } from '../../../utils/entityId';
import { isFinished, isRainedOut, matchScoreLine, winnerSide } from '../leagueGames';
import { eventTimeZone, formatEventClock, formatEventTime } from '../../../utils/eventTime';

const VIEWS = [
  { key: 'upcoming', label: 'Upcoming & live' },
  { key: 'completed', label: 'Completed' },
];

const matchTime = (m) => new Date(m.scheduledAt || m.scheduled_at || 0).getTime();

/** "Tue, Dec 15, 2026 · 8:30 PM" at the venue (utils/eventTime) */
const whenLabel = (m) => {
  const raw = m.scheduledAt || m.scheduled_at;
  if (!raw) return null;
  const zone = eventTimeZone(m);
  return `${formatEventTime(raw, zone, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} · ${formatEventClock(raw, zone)}`;
};

const statusBadge = (m) => {
  if (m.status === 'in_progress') return { label: 'LIVE', css: 'live' };
  if (m.status === 'cancelled') return isRainedOut(m) ? { label: 'Rained out', css: 'rained' } : { label: 'Cancelled', css: 'cancelled' };
  if (m.status === 'completed') return { label: 'Final', css: 'final' };
  return { label: 'Scheduled', css: 'scheduled' };
};

/**
 * LeagueMatchesTab
 *
 * Two sub-tabs: Upcoming & live (live first, then soonest) and Completed
 * (newest first, rained-out games included). Each card shows the teams, the
 * final score with the winner highlighted, date, time, court, referee team and
 * a Seeding tag. Lists page 50 at a time from the server.
 *
 * Props: leagueMatches, meta { view, total, counts }, onViewChange(view),
 * onLoadMore(), loadingMore, loading, navigate, leagueId. Without
 * onViewChange the list is split client side.
 */
const LeagueMatchesTab = ({
  leagueMatches,
  meta = null,
  onViewChange,
  onLoadMore,
  loadingMore = false,
  loading,
  navigate,
  leagueId,
}) => {
  const [localView, setLocalView] = useState('upcoming');
  const view = onViewChange ? (meta?.view || 'upcoming') : localView;
  const userPicked = useRef(false);

  const counts = useMemo(() => {
    if (meta?.counts) return meta.counts;
    const all = leagueMatches || [];
    const completed = all.filter((m) => isFinished(m.status)).length;
    return { upcoming: all.length - completed, completed, total: all.length };
  }, [meta, leagueMatches]);

  const items = useMemo(() => {
    const all = leagueMatches || [];
    if (onViewChange) return all;
    const inView = all.filter((m) => (view === 'completed' ? isFinished(m.status) : !isFinished(m.status)));
    return view === 'completed'
      ? inView.sort((a, b) => matchTime(b) - matchTime(a))
      : inView.sort((a, b) => (b.status === 'in_progress') - (a.status === 'in_progress') || matchTime(a) - matchTime(b));
  }, [leagueMatches, onViewChange, view]);

  const selectView = (next, byUser = true) => {
    if (byUser) userPicked.current = true;
    if (next === view) return;
    if (onViewChange) onViewChange(next);
    else setLocalView(next);
  };

  // A finished season opens on its results
  useEffect(() => {
    if (!userPicked.current && view === 'upcoming' && counts.upcoming === 0 && counts.completed > 0) {
      selectView('completed', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts.upcoming, counts.completed]);

  const handleClick = (match) => {
    navigate(`/matches/${eid(match)}`, {
      state: { from: `/leagues/${leagueId}`, tab: 'matches' },
    });
  };

  // Logo or player photo with the name below, VS between; the winner gets the crown
  const renderTeams = (match, winner) => {
    const [home, away] = matchSides(match);
    return (
      <Matchup
        size="md"
        className="league-match-card__matchup"
        home={{ ...home, winner: winner === 'participant1' }}
        away={{ ...away, winner: winner === 'participant2' }}
      />
    );
  };

  const renderCard = (match, index) => {
    const badge = statusBadge(match);
    const winner = winnerSide(match);
    const score = match.status === 'completed' ? matchScoreLine(match) : null;
    const when = whenLabel(match);
    return (
      <button
        type="button"
        key={match.id || index}
        className={`league-match-card league-match-card--${badge.css}`}
        onClick={() => handleClick(match)}
      >
        <div className="league-match-card__head">
          <span className={`league-match-card__badge league-match-card__badge--${badge.css}`}>{badge.label}</span>
          {match.seeding_game && <span className="league-match-card__tag">Seeding</span>}
        </div>
        {renderTeams(match, winner)}
        {score && <div className="league-match-card__score">{score}</div>}
        <div className="league-match-card__meta">
          {when && <span>📅 {when}</span>}
          {match.court && <span>📍 {match.court}</span>}
          {match.referee_team_name && <span>🧑‍⚖️ Ref: {match.referee_team_name}</span>}
        </div>
      </button>
    );
  };

  if (loading) {
    return <div className="empty-content"><p>Loading matches…</p></div>;
  }

  if (!counts.total) {
    return (
      <div className="matches-content">
        <div className="content-header"><h3>League Matches</h3></div>
        <div className="empty-content">
          <p>No matches yet.</p>
          <p>Generate a schedule and confirm matches to start playing!</p>
        </div>
      </div>
    );
  }

  const shownTotal = onViewChange ? (meta?.total ?? items.length) : items.length;

  return (
    <div className="matches-content">
      <div className="content-header">
        <h3>League Matches</h3>
      </div>

      <div className="league-subtabs" role="tablist" aria-label="Matches">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            role="tab"
            aria-selected={view === v.key}
            className={`league-subtab${view === v.key ? ' league-subtab--active' : ''}`}
            onClick={() => selectView(v.key)}
          >
            {v.label} ({counts[v.key] ?? 0})
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="empty-content">
          <p>{view === 'completed' ? 'No completed matches yet.' : 'Nothing upcoming: every match has been played.'}</p>
        </div>
      ) : (
        <div className="league-match-grid">{items.map(renderCard)}</div>
      )}

      {onLoadMore && items.length < shownTotal && (
        <div className="league-load-more">
          <button type="button" className="league-load-more__btn" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : `Load more (${shownTotal - items.length} more)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default LeagueMatchesTab;
